import h from "trader-hyperscript";
import { Source } from "trader.ts/types/source";
import { dyn, dynVariadic, scheduleForCleanup } from "trader.ts/ui";
import { Field, Parsing } from "trader.ts/ui.forms";
import * as style from "./../../styles/style.css.classes";
import * as FA from "./../../styles/fontawesome.css.classes";

export function typeaheadField<T>(
  rpcForOptions: (opts: { searchstring: string }) => Promise<T[]>,
  renderOption: (t: T) => string,
  opts: {
    maxCandidatesToShow: number;
    initial: null | T;
    label: string;
  },
): Field<T> {
  const s: Source<Parsing<T>> = new Source(
    opts.initial === null
      ? {
          tag: "initial",
        }
      : {
          tag: "parsed",
          parsed: opts.initial,
        },
  );
  let initialVal = "";
  return {
    s,
    cleanup: () => {},
    render: () => {
      return (
        <div>
          <label>{opts.label}</label>
          {dyn(s, function (val) {
            if (val.tag === "parsed") {
              const startEditing = () => {
                initialVal = renderOption(val.parsed);
                s.set({ tag: "initial" });
                setTimeout(() => {
                  (
                    document.querySelector("#enrollment-form-input") as
                      | HTMLElement
                      | undefined
                  )?.focus();
                }, 5);
              };
              const fakeSelectBox = (
                <a
                  id="fake-select-box"
                  className={style.autocomplete + style.input_like}
                  onclick={startEditing}
                >
                  <span>{renderOption(val.parsed)}</span>
                  <div style="font-size: 0.7em;">▼</div>
                </a>
              );
              fakeSelectBox.addEventListener("keydown", (ev) =>
                ev.key === "Enter" ? startEditing() : {},
              );
              fakeSelectBox.setAttribute("tabindex", "0");
              return fakeSelectBox;
            } else {
              const selectCandidate = (t: T) => {
                s.set({ tag: "parsed", parsed: t });
                setTimeout(() => {
                  (
                    document.querySelector("#fake-select-box") as
                      | HTMLElement
                      | undefined
                  )?.focus();
                }, 5);
              };
              return renderInputWithTypeahead(
                rpcForOptions,
                renderOption,
                selectCandidate,
                { maxCandidatesToShow: opts.maxCandidatesToShow, initialVal },
              );
            }
          })}
        </div>
      );
    },
  };
}

function renderInputWithTypeahead<T>(
  rpcForOptions: (opts: { searchstring: string }) => Promise<T[]>,
  renderOption: (t: T) => string,
  selectCandidate: (t: T) => void,
  opts: {
    maxCandidatesToShow: number;
    initialVal: string;
  },
) {
  const candidatesS: Source<T[]> = new Source([]);
  const selectedIndexS: Source<null | number> = new Source(null);
  const textS = new Source("");

  scheduleForCleanup(
    textS.observe((searchstring) =>
      rpcForOptions({
        searchstring,
      }).then((stus) => {
        candidatesS.set(stus.orderBy(renderOption));
        selectedIndexS.set(0);
      }),
    ),
  );

  const i = (
    <input type="text" id="enrollment-form-input" style="margin-bottom: 0px" />
  ) as HTMLInputElement;

  i.oninput = () => {
    textS.set(i.value);
  };
  scheduleForCleanup(textS.observe((v) => (i.value = v)));
  i.onkeydown = (ev) => {
    if (ev.key === "ArrowDown") {
      ev.preventDefault();
      selectedIndexS.set(
        Math.min(candidatesS.get().length - 1, (selectedIndexS.get() || 0) + 1),
      );
    } else if (ev.key === "ArrowUp") {
      ev.preventDefault();
      selectedIndexS.set(
        Math.min(
          candidatesS.get().length - 1,
          opts.maxCandidatesToShow - 1,
          (selectedIndexS.get() || 0) - 1,
        ),
      );
    } else if (ev.key === "Enter") {
      ev.preventDefault();
      const selI = selectedIndexS.get();
      if (selI !== null) {
        const selCan = candidatesS.get()[selI];
        if (selCan) {
          selectCandidate(selCan);
        }
      }
    }
  };

  if (opts.initialVal) {
    textS.set(opts.initialVal);
  }

  return (
    <div className={style.autocomplete}>
      {i}
      <ul>
        {dynVariadic(
          [candidatesS, selectedIndexS],
          ([candidates, selectedIndex]) =>
            candidates.take(opts.maxCandidatesToShow).map((c, i) => (
              <li>
                <a
                  style={
                    i === selectedIndex
                      ? "background-color: var(--accent-100); color: var(--accent-900);"
                      : ""
                  }
                  onclick={() => selectCandidate(c)}
                >
                  {renderOption(c)}
                </a>
              </li>
            )),
        )}
      </ul>
    </div>
  );
}

export function typeaheadFieldMulti<T>(
  rpcForOptions: (opts: { searchstring: string }) => Promise<T[]>,
  renderOption: (t: T) => string,
  opts: {
    maxCandidatesToShow: number;
    initial: T[];
    label: string;
  },
): Field<T[]> {
  const s: Source<Parsing<T[]>> = new Source({
    tag: "parsed",
    parsed: opts.initial,
  });
  return {
    s,
    cleanup: () => {},
    render: () => {
      function remove(i: number) {
        const val = s.get();
        if (val.tag !== "parsed") {
        } else {
          s.set({
            tag: "parsed",
            parsed: val.parsed.slice(0, i).concat(val.parsed.slice(i + 1)),
          });
        }
      }
      return (
        <article>
          <label>{opts.label}</label>
          {dyn(s, function (val) {
            if (val.tag === "parsed") {
              const selectedOptions = val.parsed.map((t, i) => (
                <div
                  style="display: flex; padding: 0px; margin-bottom: 8px;"
                  className={style.panel}
                >
                  <div style="flex: 1; padding: 8px 16px;">
                    {renderOption(t)}
                  </div>
                  <button
                    onclick={() => remove(i)}
                    className={
                      style.button_em_down + style.button_has_only_icon
                    }
                  >
                    <i className={FA.icon_trash_alt_solid}></i>
                  </button>
                </div>
              ));
              const selectCandidate = (t: T) => {
                s.set({ tag: "parsed", parsed: [t].concat(val.parsed) });
                setTimeout(() => {
                  (
                    document.querySelector("#enrollment-form-input") as
                      | HTMLElement
                      | undefined
                  )?.focus();
                }, 5);
              };
              const i = renderInputWithTypeahead(
                (ss) =>
                  rpcForOptions(ss).then((candidates) => {
                    const val = s.get();
                    const curr = val.tag === "parsed" ? val.parsed : [];
                    return candidates.filter(
                      (cand) =>
                        !curr.some(
                          (c) => renderOption(c) === renderOption(cand),
                        ),
                    );
                  }),
                renderOption,
                selectCandidate,
                {
                  maxCandidatesToShow: opts.maxCandidatesToShow,
                  initialVal: "",
                },
              );
              return (
                <div>
                  {i}
                  {selectedOptions.length > 0 ? (
                    <div style="margin-top: -8px;">{selectedOptions}</div>
                  ) : (
                    <span></span>
                  )}
                </div>
              );
            } else {
              return <span></span>;
            }
          })}
        </article>
      );
    },
  };
}
